
import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useLeads } from "@/context/LeadContext";
import { phpApi } from "@/integrations/php/client";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft,
  Phone,
  MapPin,
  Calendar,
  Mail,
  ExternalLink,
  User,
  Clock,
  MessageSquare,
  FileText,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Copy,
  Eye,
  Megaphone,
  Newspaper,
  Users
} from "lucide-react";
import { LeadStageType } from "@/context/LeadContext";
import LeadNotesPanel from "@/components/leads/LeadNotesPanel";
import { StickyNote } from "lucide-react";
import { format } from "date-fns";
import { el } from "date-fns/locale";
import { useToast } from "@/components/ui/use-toast";
import { EmptyState } from "@/components/ui/EmptyState";
import { parseLeadComments } from "@/utils/leadComments";
import { fetchAdminLead } from "@/api/adminLeads";
import { useQuery } from "@tanstack/react-query";
import { LeadAssignmentPanel } from "@/components/leads/LeadAssignmentPanel";
import LeadFormAnswers from '@/components/leads/LeadFormAnswers';
import { customerTimeframeAnswer } from '@/utils/leadFormAnswers';

const AdminLeadDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { getLeadById, updateLeadStage, refreshLeads } = useLeads();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("details");
  const [isRegeocoding, setIsRegeocoding] = useState(false);

  const contextLead = id ? getLeadById(id) : undefined;
  const { data: lead, isPending, isError, refetch } = useQuery({
    queryKey: ['admin-lead-detail', id],
    queryFn: () => fetchAdminLead(id!),
    enabled: Boolean(id),
    initialData: contextLead,
    refetchInterval: 30000,
  });
  const refreshCurrentLead = async () => {
    const result = await refetch();
    if (result.isError) throw result.error;
    await refreshLeads();
  };

  const handleBack = () => {
    navigate("/admin/leads");
  };

  const handleUpdateStage = async (stage: LeadStageType) => {
    if (id) {
      try {
        await updateLeadStage(id, stage);
        await refreshCurrentLead();
        toast({
          title: "Επιτυχής ενημέρωση",
          description: "Η κατάσταση του ενδιαφερόμενου ενημερώθηκε.",
        });
      } catch (error) {
        console.error("Error updating stage:", error);
        toast({
          title: "Αποτυχία ενημέρωσης",
          description: "Υπήρξε πρόβλημα με την ενημέρωση της κατάστασης.",
          variant: "destructive",
        });
      }
    }
  };

  const handleRegeocode = async () => {
    if (!id) return;
    setIsRegeocoding(true);
    try {
      const { data, error } = await phpApi.functions.invoke('fix-lead-geocoding', { body: { lead_id: id } });
      if (error) throw new Error(error.message);
      if (data?.success) {
        toast({
          title: "Geocoding ενημερώθηκε",
          description: data.partner_assigned
            ? "Coordinates διορθώθηκαν και ο ενδιαφερόμενος ανατέθηκε αυτόματα."
            : "Coordinates διορθώθηκαν. Επιλέξτε συνεργάτη από τη λίστα.",
        });
        await refreshCurrentLead();
      } else {
        throw new Error(data?.error || "Geocoding failed");
      }
    } catch (err) {
      toast({ title: "Σφάλμα geocoding", description: err.message, variant: "destructive" });
    } finally {
      setIsRegeocoding(false);
    }
  };


  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "dd MMM yyyy, HH:mm", { locale: el });
    } catch (e) {
      return "Άγνωστη ημερομηνία";
    }
  };

  if (isPending) return <div role="status" className="py-16 text-center text-muted-foreground">Φόρτωση επαφής…</div>;

  if (!lead) {
    return (
      <div className="py-16 animate-fade-in">
        <EmptyState
          icon={<User />}
          title={isError ? "Δεν φορτώθηκε η επαφή" : "Ο ενδιαφερόμενος δεν βρέθηκε"}
          description={isError ? "Ελέγξτε τη σύνδεσή σας και δοκιμάστε ξανά." : "Δεν βρέθηκαν στοιχεία για την επαφή που αναζητάτε."}
          action={
            <Button onClick={handleBack} className="btn-gradient">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Επιστροφή στη λίστα
            </Button>
          }
        />
      </div>
    );
  }

  // ── Derived display values ──
  const stageLabel: string = (() => {
    const map: Record<string, string> = {
      Νέο: "Νέο",
      σεΕκκρεμότητα: "Σε Εκκρεμότητα",
      ήθελεΠληροφορίες: "Ήθελε Πληροφορίες",
      έκλεισεΗΔουλειά: "Έκλεισε η Δουλειά",
      δενΑπάντησε: "Δεν Απάντησε",
    };
    return map[lead.stage] ?? lead.stage;
  })();

  const stageBadgeClass: string = (() => {
    const map: Record<string, string> = {
      Νέο: "bg-violet-500/20 border-violet-400/30 text-violet-300",
      σεΕκκρεμότητα: "bg-amber-500/20 border-amber-400/30 text-amber-300",
      ήθελεΠληροφορίες: "bg-blue-500/20 border-blue-400/30 text-blue-300",
      έκλεισεΗΔουλειά: "bg-emerald-500/20 border-emerald-400/30 text-emerald-300",
      δενΑπάντησε: "bg-slate-500/20 border-slate-400/30 text-slate-300",
    };
    return map[lead.stage] ?? "bg-slate-500/20 border-slate-400/30 text-slate-300";
  })();

  const stageDotClass: string = (() => {
    const map: Record<string, string> = {
      Νέο: "bg-violet-400",
      σεΕκκρεμότητα: "bg-amber-400",
      ήθελεΠληροφορίες: "bg-blue-400",
      έκλεισεΗΔουλειά: "bg-emerald-400",
      δενΑπάντησε: "bg-slate-400",
    };
    return map[lead.stage] ?? "bg-slate-400";
  })();

  const serviceLabel: string = (() => {
    const map: Record<string, string> = {
      εξοικονομώ: "Εξοικονομώ",
      αντλίεςΘερμότητας: "Αντλίες Θερμότητας",
      φωτοβολταϊκά: "Φωτοβολταϊκά",
      κλιματιστικά: "Κλιματιστικά",
      φυσικόΑέριο: "Φυσικό Αέριο",
      ηλιακά: "Ηλιακά",
      ανακαινίζωΕνεργειακά: "Ανακαινίζω - Ενεργειακά συστήματα",
      ανακαινίζωΠλήρηςΑνακαίνιση: "Ανακαινίζω - Πλήρης ανακαίνιση",
      άλλο: "Άλλο",
    };
    return map[lead.service] ?? lead.service;
  })();

  const timeframeLabel: string = (() => {
    const customerAnswer = customerTimeframeAnswer(lead.formAnswers);
    if (customerAnswer) return customerAnswer;
    if (lead.timeframe === "άμεσα") return "Άμεσα";
    if (lead.timeframe === "εντός3μηνών") return "Εντός 3 μηνών";
    if (lead.timeframe === "κάνωΈρευνα") return "Έρευνα Αγοράς";
    return lead.timeframe;
  })();

  const timeframeClass: string = (() => {
    if (lead.timeframe === "άμεσα") return "bg-red-500/20 border-red-400/30 text-red-300";
    if (lead.timeframe === "εντός3μηνών") return "bg-amber-500/20 border-amber-400/30 text-amber-300";
    return "bg-white/10 border-white/20 text-white/60";
  })();

  const smsBadge: string = (() => {
    if (!lead.smsStatus) return "Δεν εστάλη";
    const map: Record<string, string> = {
      sent: "Εστάλη",
      delivered: "Παραδόθηκε",
      failed: "Απέτυχε",
    };
    return map[lead.smsStatus] ?? lead.smsStatus;
  })();

  const initials = lead.name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="space-y-6 animate-fade-in">

      {/* ── HERO CARD ── */}
      <Card className="border-0 shadow-xl overflow-hidden">
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative">
          {/* decorative blurs */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-violet-500/10 rounded-full blur-2xl" />
          </div>

          <div className="relative p-6 md:p-8">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">

              {/* Left: back button + avatar + name + info */}
              <div className="flex items-start gap-5">
                <button
                  onClick={handleBack}
                  className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors mt-1"
                >
                  <ArrowLeft className="h-4 w-4 text-white/80" />
                </button>

                {/* Initials avatar */}
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center shadow-lg flex-shrink-0">
                  <span className="text-2xl font-bold text-white">{initials}</span>
                </div>

                <div>
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <h1 className="text-2xl md:text-3xl font-bold text-white">{lead.name}</h1>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${stageBadgeClass}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${stageDotClass}`} />
                      {stageLabel}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-white/60">
                    <a
                      href={`tel:${lead.phone}`}
                      className="flex items-center gap-1.5 hover:text-white transition-colors"
                    >
                      <Phone className="h-3.5 w-3.5" />{lead.phone}
                    </a>
                    <span className="flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5" />{lead.address}
                    </span>
                    {lead.email && (
                      <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 hover:text-white transition-colors">
                        <Mail className="h-3.5 w-3.5" />{lead.email}
                      </a>
                    )}
                    <span className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" />Δημ. {formatDate(lead.createdAt)}
                    </span>
                  </div>

                  {/* Service + timeframe + campaign tags */}
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    <span className="px-2.5 py-0.5 rounded-full bg-white/10 border border-white/20 text-white/80 text-xs">
                      {serviceLabel}
                    </span>
                    <span className={`px-2.5 py-0.5 rounded-full border text-xs ${timeframeClass}`}>
                      {timeframeLabel}
                    </span>
                    {lead.campaign && (
                      <span className="px-2.5 py-0.5 rounded-full bg-violet-500/20 border border-violet-400/30 text-violet-300 text-xs max-w-[220px] truncate" title={lead.campaign}>
                        📣 {lead.campaign}
                      </span>
                    )}
                    {lead.source === 'facebook_api' ? (
                      <span className="px-2.5 py-0.5 rounded-full bg-violet-500/20 border border-violet-400/30 text-violet-300 text-xs">
                        ⚡ Meta API
                      </span>
                    ) : lead.source === 'facebook_sheets' ? (
                      <span className="px-2.5 py-0.5 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-300 text-xs">
                        📊 Sheets
                      </span>
                    ) : lead.source === 'facebook_leads' ? (
                      <span className="px-2.5 py-0.5 rounded-full bg-indigo-500/20 border border-indigo-400/30 text-indigo-300 text-xs">
                        📣 FB Ads
                      </span>
                    ) : (
                      <span className="px-2.5 py-0.5 rounded-full bg-slate-500/20 border border-slate-400/30 text-slate-300 text-xs">
                        🌐 Web
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Right: action buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => window.open(`/lead/${lead.id}`, "_blank")}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 text-white/70 hover:text-white hover:bg-white/20 transition-all text-sm"
                >
                  <ExternalLink className="h-4 w-4" /> Προβολή Συνεργάτη
                </button>
                {lead.viewed && (
                  <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 text-xs">
                    <Eye className="h-3.5 w-3.5" /> Προβλήθηκε
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Bottom KPI strip */}
          <div className="border-t border-white/10 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            <div className="px-6 py-4 flex items-center gap-3">
              <Users className="h-4 w-4 text-white/40 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white truncate">{lead.partnerName || "Χωρίς ανάθεση"}</p>
                <p className="text-xs text-white/50">Συνεργάτης</p>
              </div>
            </div>
            <div className="px-6 py-4 flex items-center gap-3">
              <MessageSquare className="h-4 w-4 text-white/40 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-white">{smsBadge}</p>
                <p className="text-xs text-white/50">SMS Status</p>
              </div>
            </div>
            <div className="px-6 py-4 flex items-center gap-3">
              <Eye className="h-4 w-4 text-white/40 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-white">{lead.viewed ? "Ναι" : "Όχι"}</p>
                <p className="text-xs text-white/50">Προβλήθηκε</p>
              </div>
            </div>
            <div className="px-6 py-4 flex items-center gap-3">
              <Clock className="h-4 w-4 text-white/40 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white truncate">{formatDate(lead.updatedAt)}</p>
                <p className="text-xs text-white/50">Τελ. ενημέρωση</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* ── TABS ── */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1 rounded-xl h-auto w-full max-w-xl grid grid-cols-4 mb-6">
          <TabsTrigger
            value="details"
            className="flex items-center gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2"
          >
            <User className="w-4 h-4" />
            Στοιχεία
          </TabsTrigger>
          <TabsTrigger
            value="notes"
            className="flex items-center gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2"
          >
            <StickyNote className="w-4 h-4" />
            Σημειώσεις
          </TabsTrigger>
          <TabsTrigger
            value="communication"
            className="flex items-center gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2"
          >
            <MessageSquare className="w-4 h-4" />
            Επικοινωνία
          </TabsTrigger>
          <TabsTrigger
            value="history"
            className="flex items-center gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2"
          >
            <Clock className="w-4 h-4" />
            Ιστορικό
          </TabsTrigger>
        </TabsList>

        {/* ── DETAILS TAB ── */}
        <TabsContent value="details" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">

            {/* Contact Info */}
            <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-blue-50">
                    <User className="h-4 w-4 text-blue-600" />
                  </div>
                  <CardTitle className="text-lg">Προσωπικά Στοιχεία</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="mt-1 p-1.5 rounded-full bg-slate-100">
                    <Phone className="h-4 w-4 text-slate-600" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-500 block">Τηλέφωνο</span>
                    <div className="flex items-center gap-2">
                      <a href={`tel:${lead.phone}`} className="text-lg font-medium text-slate-900 hover:text-blue-600">
                        {lead.phone}
                      </a>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600"
                        onClick={() => {
                          navigator.clipboard.writeText(lead.phone);
                          toast({ title: "Αντιγράφηκε", description: `Το ${lead.phone} αντιγράφηκε στο πρόχειρο.` });
                        }}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>

                {lead.email && (
                  <div className="flex items-start gap-3">
                    <div className="mt-1 p-1.5 rounded-full bg-slate-100">
                      <Mail className="h-4 w-4 text-slate-600" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-slate-500 block">Email</span>
                      <a href={`mailto:${lead.email}`} className="text-base text-slate-900 hover:text-blue-600">{lead.email}</a>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <div className="mt-1 p-1.5 rounded-full bg-slate-100">
                    <MapPin className="h-4 w-4 text-slate-600" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-500 block">Διεύθυνση</span>
                    <span className="text-base text-slate-900 block">{lead.address}</span>
                    <span className="text-sm text-slate-500">ΤΚ: {lead.postalCode}</span>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {(lead.latitude && lead.longitude) ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Γεωγραφικό Στίγμα Επιβεβαιωμένο
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          Χωρίς Γεωγραφικό Στίγμα
                        </Badge>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRegeocode}
                        disabled={isRegeocoding}
                        className="h-6 text-xs px-2 py-0"
                      >
                        {isRegeocoding ? "…" : "🔄 Re-geocode"}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Interest Details */}
            <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-violet-50">
                    <FileText className="h-4 w-4 text-violet-600" />
                  </div>
                  <CardTitle className="text-lg">Λεπτομέρειες Ενδιαφέροντος</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-slate-500 block mb-2">Υπηρεσία</span>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-sm font-medium">
                      {serviceLabel}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-500 block mb-2">Χρονοδιάγραμμα</span>
                    <Badge
                      variant="outline"
                      className={`
                        ${lead.timeframe === "άμεσα" ? "bg-red-50 text-red-700 border-red-200" :
                          lead.timeframe === "εντός3μηνών" ? "bg-amber-50 text-amber-700 border-amber-200" :
                            "bg-slate-50 text-slate-700 border-slate-200"}
                      `}
                    >
                      {timeframeLabel}
                    </Badge>
                  </div>
                </div>

                <LeadFormAnswers answers={lead.formAnswers} />

                {lead.comments && (() => {
                  const meta = parseLeadComments(lead.comments);
                  const hasCampaignMeta = meta.campaign || meta.form || meta.ad || meta.organic !== undefined;
                  return (
                    <div className="space-y-3">
                      {/* Warnings first — they need attention */}
                      {meta.warnings.map((w, i) => (
                        <div key={i} className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                          <div className="text-sm text-amber-800 min-w-0">
                            {w.text}
                            {w.linkedLeadId && (
                              <Link
                                to={`/admin/lead/${w.linkedLeadId}`}
                                className="ml-2 inline-flex items-center gap-1 font-medium text-amber-900 underline underline-offset-2 hover:text-amber-700"
                              >
                                Δες το <ExternalLink className="h-3 w-3" />
                              </Link>
                            )}
                          </div>
                        </div>
                      ))}

                      {/* Campaign metadata — structured, not a text blob */}
                      {hasCampaignMeta && (
                        <div className="p-4 bg-violet-50/50 rounded-lg border border-violet-100">
                          <span className="text-xs font-semibold uppercase tracking-wider text-violet-500 block mb-3">Στοιχεία Καμπάνιας</span>
                          <div className="space-y-2">
                            {meta.campaign && (
                              <div className="flex items-center gap-2 text-sm min-w-0">
                                <Megaphone className="h-3.5 w-3.5 text-violet-500 shrink-0" />
                                <span className="text-slate-500 shrink-0">Καμπάνια:</span>
                                <span className="text-slate-800 font-medium truncate" title={meta.campaign}>{meta.campaign}</span>
                              </div>
                            )}
                            {meta.form && (
                              <div className="flex items-center gap-2 text-sm min-w-0">
                                <FileText className="h-3.5 w-3.5 text-violet-500 shrink-0" />
                                <span className="text-slate-500 shrink-0">Φόρμα:</span>
                                <span className="text-slate-800 font-medium truncate" title={meta.form}>{meta.form}</span>
                              </div>
                            )}
                            {meta.ad && (
                              <div className="flex items-center gap-2 text-sm min-w-0">
                                <Newspaper className="h-3.5 w-3.5 text-violet-500 shrink-0" />
                                <span className="text-slate-500 shrink-0">Διαφήμιση:</span>
                                <span className="text-slate-800 font-medium truncate" title={meta.ad}>{meta.ad}</span>
                              </div>
                            )}
                            {meta.organic !== undefined && (
                              <Badge
                                variant="outline"
                                className={meta.organic
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : "bg-blue-50 text-blue-700 border-blue-200"}
                              >
                                {meta.organic ? "Οργανικό (χωρίς πληρωμή)" : "Πληρωμένη διαφήμιση"}
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Genuine free-text comments from the customer */}
                      {meta.freeText && (
                        <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-2">Σχόλια</span>
                          <p className="text-sm text-slate-700 italic">"{meta.freeText}"</p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            <LeadAssignmentPanel key={lead.id} lead={lead} onChanged={refreshCurrentLead}>
              <div className="space-y-3">
                <span className="text-sm font-medium text-slate-500 block">Ενημέρωση Κατάστασης</span>
                <div className="flex flex-wrap gap-2">
                  {([
                    { stage: "σεΕκκρεμότητα",    label: "Σε Εκκρεμότητα",    active: "bg-amber-100 text-amber-800 border-amber-300 ring-2 ring-amber-200",   inactive: "border-amber-200 text-amber-700 hover:bg-amber-50" },
                    { stage: "ήθελεΠληροφορίες", label: "Ήθελε Πληροφορίες", active: "bg-indigo-100 text-indigo-800 border-indigo-300 ring-2 ring-indigo-200", inactive: "border-indigo-200 text-indigo-700 hover:bg-indigo-50" },
                    { stage: "δενΑπάντησε",       label: "Δεν Απάντησε",       active: "bg-slate-200 text-slate-800 border-slate-300 ring-2 ring-slate-200",    inactive: "border-slate-300 text-slate-600 hover:bg-slate-50" },
                    { stage: "έκλεισεΗΔουλειά",  label: "Έκλεισε η Δουλειά", active: "bg-emerald-100 text-emerald-800 border-emerald-300 ring-2 ring-emerald-200", inactive: "border-emerald-200 text-emerald-700 hover:bg-emerald-50" },
                  ] as const).map(({ stage, label, active, inactive }) => (
                    <button
                      key={stage}
                      onClick={() => handleUpdateStage(stage as LeadStageType)}
                      disabled={lead.stage === stage}
                      className={`rounded-full px-3.5 py-1.5 text-sm font-medium border transition-all ${lead.stage === stage ? active : inactive}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </LeadAssignmentPanel>
          </div>
        </TabsContent>

        {/* ── COMMUNICATION TAB ── */}
        {/* ── NOTES TAB ── */}
        <TabsContent value="notes" className="space-y-6">
          {id && (
            <LeadNotesPanel
              leadId={id}
              authorType="admin"
              authorName="Διαχειριστής"
            />
          )}
        </TabsContent>

        <TabsContent value="communication">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-indigo-50">
                  <MessageSquare className="h-4 w-4 text-indigo-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Επικοινωνία</CardTitle>
                  <CardDescription>Διαχείριση μηνυμάτων και ιστορικό επικοινωνίας</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="bg-slate-50 rounded-xl p-8 text-center border border-slate-200 border-dashed">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                  <Mail className="h-6 w-6 text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">Κέντρο Μηνυμάτων</h3>
                <p className="text-slate-500 max-w-sm mx-auto mb-6">
                  Για να στείλετε SMS ή να δείτε το πλήρες ιστορικό επικοινωνίας, παρακαλώ μεταβείτε στη σελίδα Μηνύματα.
                </p>
                <Button onClick={() => navigate("/admin/messages")} className="btn-gradient">
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Μετάβαση στα Μηνύματα
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── HISTORY TAB ── */}
        <TabsContent value="history">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-slate-100">
                  <Clock className="h-4 w-4 text-slate-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Χρονολόγιο</CardTitle>
                  <CardDescription>Ιστορικό ενεργειών και αλλαγών</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="relative border-l-2 border-slate-200 ml-4 space-y-6 py-2">

                {/* Created */}
                <div className="relative pl-8">
                  <div className="absolute -left-[9px] top-1 h-4 w-4 rounded-full bg-white border-2 border-slate-400 shadow-sm" />
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 hover:border-slate-200 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-2">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-slate-200">
                          <FileText className="h-3.5 w-3.5 text-slate-600" />
                        </div>
                        <p className="font-semibold text-slate-900 text-sm">Δημιουργία Lead</p>
                      </div>
                      <span className="text-xs text-slate-500 font-mono bg-white px-2.5 py-1 rounded-lg border border-slate-200 shadow-sm">
                        {formatDate(lead.createdAt)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 pl-8">Εισαγωγή στο σύστημα</p>
                  </div>
                </div>

                {/* Viewed */}
                {lead.viewed && lead.viewedAt && (
                  <div className="relative pl-8">
                    <div className="absolute -left-[9px] top-1 h-4 w-4 rounded-full bg-white border-2 border-blue-400 shadow-sm" />
                    <div className="p-4 rounded-xl bg-blue-50 border border-blue-100 hover:border-blue-200 transition-colors">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-2">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 rounded-lg bg-blue-100">
                            <Eye className="h-3.5 w-3.5 text-blue-600" />
                          </div>
                          <p className="font-semibold text-slate-900 text-sm">Προβολή από Συνεργάτη</p>
                        </div>
                        <span className="text-xs text-slate-500 font-mono bg-white px-2.5 py-1 rounded-lg border border-blue-100 shadow-sm">
                          {formatDate(lead.viewedAt)}
                        </span>
                      </div>
                      <p className="text-sm text-blue-700 pl-8">
                        Ο συνεργάτης άνοιξε την καρτέλα του lead
                      </p>
                    </div>
                  </div>
                )}

                {/* SMS Status */}
                {lead.smsStatus && (
                  <div className="relative pl-8">
                    <div className={`absolute -left-[9px] top-1 h-4 w-4 rounded-full bg-white border-2 shadow-sm ${
                      lead.smsStatus === "sent" || lead.smsStatus === "delivered"
                        ? "border-emerald-400"
                        : "border-red-400"
                    }`} />
                    <div className={`p-4 rounded-xl border transition-colors ${
                      lead.smsStatus === "sent" || lead.smsStatus === "delivered"
                        ? "bg-emerald-50 border-emerald-100 hover:border-emerald-200"
                        : "bg-red-50 border-red-100 hover:border-red-200"
                    }`}>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-2">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded-lg ${
                            lead.smsStatus === "sent" || lead.smsStatus === "delivered"
                              ? "bg-emerald-100"
                              : "bg-red-100"
                          }`}>
                            <MessageSquare className={`h-3.5 w-3.5 ${
                              lead.smsStatus === "sent" || lead.smsStatus === "delivered"
                                ? "text-emerald-600"
                                : "text-red-600"
                            }`} />
                          </div>
                          <p className="font-semibold text-slate-900 text-sm">
                            {lead.smsStatus === "sent" || lead.smsStatus === "delivered"
                              ? "Επιτυχής Αποστολή SMS"
                              : "Αποτυχία Αποστολής SMS"}
                          </p>
                        </div>
                        <span className="text-xs text-slate-500 font-mono bg-white px-2.5 py-1 rounded-lg border border-slate-200 shadow-sm">
                          {formatDate(lead.updatedAt)}
                        </span>
                      </div>
                      <p className={`text-sm pl-8 ${
                        lead.smsStatus === "sent" || lead.smsStatus === "delivered"
                          ? "text-emerald-700"
                          : "text-red-700"
                      }`}>
                        {lead.smsStatus === "sent" || lead.smsStatus === "delivered"
                          ? "Το μήνυμα εστάλη επιτυχώς στον συνεργάτη"
                          : "Παρουσιάστηκε σφάλμα κατά την αποστολή"}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminLeadDetails;
